import { UiElement } from "../UiElement.js";
import { HTML5Video } from "./HTML5Video.js";
import { Thumbnail } from "./ThumbnailVideoPlayer.js";
import { CDN } from "../../CDN.js";
import { VectorGraphics } from "./VectorGraphics.js";

export { VideoPlayer };

/**
 * Video player class using Shaka Player
 * @extends UiElement
 * @memberof vef.ui.media
 * @author ckraemme <ckraemme@awi.de>
 */
class VideoPlayer extends UiElement {
    /**
     * Build a video player based on Shaka Player and include it to a target element
     * @param {string/HTMLElement} target - HTML target element
     * @param {Object.<string, Array>} options - Dictionary with input configuration for the player. The configuration can be set for the keys media, thumbnail, cover and poster. They contain the respective urls. If media is a list, the player will try to play the next item if the previous ones failed.
     * <pre>
     * Example:
     * const options = {
            'media': ['https://.../stream/master.mpd', 'https://.../stream/master.m3u8'],
            'thumbnail': 'https://.../thumbnail/master.vtt',
            'cover': 'https://.../cover/image.webp,
            'poster': 'https://.../poster/image.jpg',
        }
     * </pre>
     */
    constructor(target, options,) {
        super(target);
        const uiElementContainer = this.getElement();
        uiElementContainer.classList.add("video-player");
        uiElementContainer.innerHTML = `
            <div class="shaka-player-container">
                <video class="shaka-player-video" />
            </div>
        `;
        this.shakaPlayer_ = new ShakaPlayer(uiElementContainer, options);
    }

    /**
     * remove the video player completely
     */
    dispose() {
        super.dispose();
        if (this.shakaPlayer_) {
            delete this.shakaPlayer_.target;
            delete this.shakaPlayer_.shakaContainer;
            delete this.shakaPlayer_.shakaVideo;
            delete this.shakaPlayer_.shakaUI;
            delete this.shakaPlayer_.shakaUIControls;
            this.shakaPlayer_.shakaPlayer.destroy().then(() => {
                delete this.shakaPlayer_;
            });
        }
        return null;
    }

}

/**
 * Handle shaka player's error messages
 * @author ckraemme <ckraemme@awi.de>
 * @memberof vef.ui.media
 * @private
 */
class ShakaPlayerErrorHandler {
    /**
     * Show the error in detail in the console 
     * @param {Object} error error object
     */
    static onError(error) {
        console.error('Error code', error.code, 'object', error);
    }

    /**
     * Handle event errors
     * @param {Event} event error event
     */
    static onErrorEvent(event) {
        const error = event.detail;
        ShakaPlayerErrorHandler.onError(error);
    }
}


/**
 * Class to build the AWI version of the Shaka Player
 * @author ckraemme <ckraemme@awi.de>
 * @memberof vef.ui.media
 * @private
 */
class ShakaPlayer {
    /**
     * Build the player
     * @param {string/HTMLElement} target - HTML element
     * @param {Object.<string,Array>} options - Dictionary with input configuration for the player. The configuration can be set for the keys media, thumbnail, cover and poster. They contain the respective urls
     */
    constructor(target, options) {
        this.target = target;
        this.mediaUrlList = typeof options['media'] === 'string' ? [options['media']] : options['media']
        this.webvttUrl = options['thumbnail'];
        this.coverUrl = options['cover'];
        this.posterUrl = options['poster'];
        this.initPlayerWithKeyFocus = options['initPlayerWithKeyFocus'];
        this.manifest;
        this.shakaContainer = this.target.querySelector(".shaka-player-container");
        this.shakaVideo = this.target.querySelector(".shaka-player-video");
        this.shakaPlayer = new shaka.Player(this.shakaVideo);
        this.shakaUI = new shaka.ui.Overlay(this.shakaPlayer, this.shakaContainer, this.shakaVideo);
        this.shakaUIControls = this.shakaUI.getControls();
        this.buildShakaPlayer_()
        window.shakaPlayer = this.shakaPlayer
        window.shakaUI = this.shakaUI;
    }

    /**
     * Configure, initialize and load media into the player. Because the Shaka UI library is used, the player instance is build automatically by the former, that triggers the event 'shaka-ui-loaded' when it is done
     * @private
     */
    buildShakaPlayer_() {
        this.initEventListeners_();
        this.applyHTML5VideoSettings_();
        this.addExperimentalABRRestriction_();
        this.applyShakaSpecificPlayerConfigurations_();
        this.loadVideoManifestAndSelectThumbnailManifest_();
    }

    /**
     * Apply default video settings provided by the HTML5 video tag
     * @private
     */
    applyHTML5VideoSettings_() {
        HTML5Video.initAudio(this.target);
        HTML5Video.initKeyControl(this.target, this.initPlayerWithKeyFocus);
        HTML5Video.disablePIP(this.target);

        let removePosterAfterVideoLoaded;
        let showPosterAfterVideoLoaded;

        if (this.coverUrl == null)
            HTML5Video.poster(this.target, this.posterUrl, showPosterAfterVideoLoaded = false, removePosterAfterVideoLoaded = true);
        else {
            HTML5Video.poster(this.target, this.posterUrl, showPosterAfterVideoLoaded = false, removePosterAfterVideoLoaded = false);
            HTML5Video.poster(this.target, this.coverUrl, showPosterAfterVideoLoaded = true, removePosterAfterVideoLoaded = false);
        }
    }

    /**
     * Initialize events to the Shaka Player that extend its functionality, apply configuration and improve error logging.
     * @private
     */
    initEventListeners_() {
        this.shakaPlayer.addEventListener('error', ShakaPlayerErrorHandler.onErrorEvent);
        this.shakaUIControls.addEventListener('error', ShakaPlayerErrorHandler.onErrorEvent);
        this.initEventsAfterVideoHasLoaded_();
    }

    /**
     * Initialize events that should trigger when video has loaded
     * @private
     */
    initEventsAfterVideoHasLoaded_() {
        this.target.querySelector('video').addEventListener('loadeddata', () => {
            this.target.querySelector('video').classList.add("loaded");
            this.configureUIShakaPlayer_();
            this.extendFunctionalityForAudio_();
            if (this.webvttUrl)
                new Thumbnail(this.target, this.webvttUrl, 20, true);
        }, false);
    }

    /**
     * Configure adaptive bitrate restriction in the Shaka Player that are based on the video player size to reduce traffic and loading time. Experimental state.
     * @private
     */
    addExperimentalABRRestriction_() {
        new ResizeObserver(() => {
            if (typeof this.shakaContainer !== 'undefined' && this.shakaPlayer.getVariantTracks() !== 'undefined') {
                const realHeight = window.devicePixelRatio * this.shakaContainer.clientHeight;
                const manifestHeights = this.shakaPlayer.getVariantTracks().map((x) => {
                    return x.height
                });
                const streamHeight = Math.min.apply(Math, manifestHeights.filter((x) => {
                    return x >= realHeight
                }));
                this.shakaPlayer.configure({
                    'abr': {
                        'restrictions': {
                            'maxHeight': streamHeight
                        }
                    }
                });
            }
        }).observe(this.shakaVideo);
    }

    /**
     * Method for applying configurations to the Shaka Player provided by its developer
     * <ul>
     * <li> <a href="https://shaka-player-demo.appspot.com/docs/api/shaka.extern.html#.StreamingConfiguration"> Streaming configuration </a> </li>
     * <li> <a href="https://shaka-player-demo.appspot.com/docs/api/shaka.extern.html#.AbrConfiguration"> ABR configuration </a> </li>
     * <li> <a href="https://shaka-player-demo.appspot.com/docs/api/shaka.extern.html#.PlayerConfiguration"> Further player configuration </a> </li>
     * </ul>
     * @private
     */
    applyShakaSpecificPlayerConfigurations_() {

        // Example: this.player.configure("streaming.rebufferingGoal", 4)
        this.shakaPlayer.configure("preferredAudioLanguage", "en");
    }

    /**
     * Selects the webVTT thumbnail manifest based on the video manifest file that can be loaded
     * @private
     */
    loadVideoManifestAndSelectThumbnailManifest_() {
        this.loadMedia_(this.shakaPlayer, this.mediaUrlList).then((manifest) => {
            this.manifest = manifest;
            let index = this.mediaUrlList.indexOf(manifest);
        }).catch(error => {
            console.error("Error", error.message);
        })
    }

    /**
     * Try to load a media file from a list of media paths as long as there are items in the list
     * @param {Object} player - class instance of the player
     * @param {Array} files - files with all media files that the player tries to load in ascending (array position) order
     * @return {string|Promise} When successful, return the path of the media file, else return Promise.reject(reason)
     * @private
     */
    loadMedia_(player, files) {
        function load(path) {
            return player.load(path).then(() => {
                //console.info('Manifest loaded: ' + path);
                return true
            })
                .catch(error => {
                    console.info('Manifest can not be loaded: ' + path);
                    ShakaPlayerErrorHandler.onError(error);
                    return false
                })
        }

        files = files.filter(n => n !== null && n !== undefined);
        return load(files[0]).then(success => {
            if (success) {
                return files[0]
            } else {
                const tmp = files.slice(1, files.length)
                if (tmp == 0) {
                    this.setErrorImage_(player);
                    return Promise.reject(new Error('No supported manifest found! Playback cannot be started!'))
                } else
                    return this.loadMedia_(player, tmp)
            }
        })
    }

    /**
     * Include user notification as poster in case of video error 
     * @param {Object} player - class instance of the player
     * @private
     */
    setErrorImage_(player) {
        const backgroundColor = '#F2F2F2'
        player.getMediaElement().poster = VectorGraphics.errorImage('Video', backgroundColor);
        player.getMediaElement().style.backgroundColor = backgroundColor;
        player.getMediaElement().style.transition = undefined;
    }

    /**
     * Configure the user interface with respect to the media content
     * <ul>
     * <li> <a href="https://shaka-player-demo.appspot.com/docs/api/tutorial-ui-customization.html"> Overflow menu and control panel configuration </a> </li>
     * <li> <a href="https://shaka-player-demo.appspot.com/docs/api/shaka.extern.html#.UIConfiguration"> UI configuration </a> </li>
     * </ul>
     * @private
     */
    configureUIShakaPlayer_() {
        let overflowMenu, controlPanel;

        if (this.shakaPlayer.isAudioOnly()) {
            overflowMenu = ["loop", "playback_rate"];
            controlPanel = ["play_pause", "time_and_duration", "spacer", "mute", "volume", "overflow_menu"]
        }
        else {
            overflowMenu = ["loop", "playback_rate", "language", "captions", "quality"]
            controlPanel = ["play_pause", "time_and_duration", "spacer", "mute", "volume", "fullscreen", "overflow_menu"]
        }

        if (!HTML5Video.hasAudio(this.target)) {
            overflowMenu = overflowMenu.filter(v => !['language'].includes(v));
            controlPanel = controlPanel.filter(v => !["mute", "volume"].includes(v));
        }

        if (['m3u8', 'mpd'].includes(this.manifest.split('.').pop().toLowerCase())) {
            if (this.shakaPlayer.isLive())
                overflowMenu = overflowMenu.filter(v => !['loop', 'playback_rate'].includes(v));
            if (!this.shakaPlayer.getAudioLanguages() || this.shakaPlayer.getAudioLanguages().length <= 1)
                overflowMenu = overflowMenu.filter(v => !['language'].includes(v));
        } else {
            overflowMenu = overflowMenu.filter(v => !['language', 'quality'].includes(v));
        }

        const keyControlViaHTML5VideoElement = true;
        let uiConfigurationOptions = {
            'controlPanelElements': controlPanel,
            'overflowMenuButtons': overflowMenu,
            'enableKeyboardPlaybackControls': !keyControlViaHTML5VideoElement,
            'playbackRates': [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2],
            'showUnbufferedStart': true
        };
        this.shakaUI.configure(uiConfigurationOptions);
    }

    /**
     * Extend the functionality of the shaka player by providing a method that prevents the control bar from hiding while playing audio
     * @private
     */
    extendFunctionalityForAudio_() {

        if (this.shakaPlayer.isAudioOnly()) {
            const setUIAlwaysVisible = true;
            const targetNode = this.target.querySelector('.shaka-controls-container');
            const config = { attributes: true, childList: false, subtree: true };

            const callback = (mutationList, observer) => {
                for (const mutation of mutationList) {
                    if (mutation.type === 'attributes' && mutation.attributeName == 'shown') {
                        if (mutation.target.getAttribute(mutation.attributeName) == undefined)
                            mutation.target.setAttribute("shown", setUIAlwaysVisible);
                    }
                }
            };
            const observer = new MutationObserver(callback);
            observer.observe(targetNode, config);
        }
    }
}
